import React from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Lightbulb as LightbulbIcon, PlayCircle as PlayCircleIcon } from '@mui/icons-material';
import { glassToggle, slideSwitch } from './glassMorphismStyles';

interface FormModeToggleProps {
  mode: 'ideas' | 'simulation';
  onChange: (mode: 'ideas' | 'simulation') => void;
}

const FormModeToggle: React.FC<FormModeToggleProps> = ({ mode, onChange }) => {
  const handleModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: 'ideas' | 'simulation' | null
  ) => {
    if (newMode !== null) {
      onChange(newMode);
    }
  };

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        mb: { xs: 1.2, sm: 2.5 },
        animation: `${slideSwitch} 0.6s ease-out`,
      }}
    >
      <ToggleButtonGroup
        value={mode}
        exclusive
        onChange={handleModeChange}
        aria-label="form mode toggle"
        sx={{
          ...glassToggle,
          p: { xs: 0.5, sm: 0.5 },
        }}
      >
        <ToggleButton 
          value="ideas" 
          aria-label="generate ideas mode"
          sx={{ 
            minWidth: { xs: '120px', sm: '170px' },
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: { xs: 1.5, sm: 2.2 },
            py: { xs: 0.6, sm: 0.8 },
          }}
        >
          <LightbulbIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: mode === 'ideas' ? 600 : 400,
              fontSize: { xs: '0.8rem', sm: '0.95rem' },
            }}
          >
            Generate Ideas
          </Typography>
        </ToggleButton>
        
        <ToggleButton 
          value="simulation" 
          aria-label="simulate post mode"
          sx={{ 
            minWidth: { xs: '120px', sm: '170px' },
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: { xs: 1.5, sm: 2.2 },
            py: { xs: 0.6, sm: 0.8 },
          }}
        >
          <PlayCircleIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: mode === 'simulation' ? 600 : 400,
              fontSize: { xs: '0.8rem', sm: '0.95rem' },
            }}
          >
            Simulate a Post
          </Typography>
        </ToggleButton>
      </ToggleButtonGroup>
      
      <Typography 
        variant="caption" 
        sx={{ 
          mt: 1, 
          color: 'rgba(255, 255, 255, 0.6)',
          textAlign: 'center',
          fontSize: { xs: '0.72rem', sm: '0.875rem' },
          display: { xs: 'none', sm: 'block' },
        }}
      >
        {mode === 'ideas' 
          ? 'Get AI-generated content ideas for your brand'
          : 'Test how your audience will react to your post'}
      </Typography>
    </Box>
  );
};

export default FormModeToggle;
